import { listMedia } from './mediaLibrary';
import { uploadedImageService } from '../services/uploadedImageService';

export type AiTemplateImageCandidate = {
  id: string;
  url: string;
  label: string;
  source: 'media-library' | 'my-photos' | 'stock';
};

/** Curated Unsplash fallbacks — only used when the business has few/no own images. */
const STOCK_FALLBACK_IMAGES: { label: string; url: string }[] = [
  {
    label: 'Black sports car',
    url: 'https://images.unsplash.com/photo-1503376780353-7e6692767b70?w=1200',
  },
  {
    label: 'Red sports car',
    url: 'https://images.unsplash.com/photo-1542362567-b07e54358753?w=1200',
  },
  {
    label: 'Car detailing wash',
    url: 'https://images.unsplash.com/photo-1601362840469-51e4d8d58785?w=1200',
  },
  {
    label: 'Car polish shine',
    url: 'https://images.unsplash.com/photo-1607860108855-64acf2078987?w=1200',
  },
  {
    label: 'Auto workshop',
    url: 'https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?w=1200',
  },
  {
    label: 'Showroom car',
    url: 'https://images.unsplash.com/photo-1562113285-6cbaa7e392f5?w=1200',
  },
  {
    label: 'Business team',
    url: 'https://images.unsplash.com/photo-1556761175-b413da4baf72?w=1200',
  },
  {
    label: 'Celebration',
    url: 'https://images.unsplash.com/photo-1492684223066-81342ee5ff30?w=1200',
  },
];

/** Soft ceiling so huge libraries stay within edge payload/timeout limits. Owned images are prioritized. */
const MAX_CANDIDATES = 120;
const MIN_OWNED_BEFORE_SKIP_STOCK = 4;

/**
 * Build image candidates for AI email template creation.
 * Priority: Media Library → My Photos → Stock (only if owned set is thin).
 * The edge function reviews these in batches of 10 until it finds fitting ones.
 */
export async function gatherEmailTemplateAiImages(
  businessId: string,
): Promise<AiTemplateImageCandidate[]> {
  const owned: AiTemplateImageCandidate[] = [];
  const seen = new Set<string>();

  const push = (item: AiTemplateImageCandidate) => {
    const url = (item.url || '').trim();
    if (!url || seen.has(url)) return;
    seen.add(url);
    owned.push(item);
  };

  try {
    const media = await listMedia(businessId);
    for (const m of media) {
      if (owned.length >= MAX_CANDIDATES) break;
      push({
        id: `media-${m.id}`,
        url: m.url,
        label: m.alt || m.file_name || 'Media library image',
        source: 'media-library',
      });
    }
  } catch (err) {
    console.warn('Failed to load media library for AI templates:', err);
  }

  try {
    const photos = await uploadedImageService.getBusinessImages(businessId);
    for (const p of photos) {
      if (owned.length >= MAX_CANDIDATES) break;
      push({
        id: `photo-${p.id}`,
        url: p.url,
        label: p.name || 'Uploaded photo',
        source: 'my-photos',
      });
    }
  } catch (err) {
    console.warn('Failed to load uploaded photos for AI templates:', err);
  }

  const ownedCount = owned.filter(
    (i) => i.source === 'media-library' || i.source === 'my-photos',
  ).length;

  if (ownedCount < MIN_OWNED_BEFORE_SKIP_STOCK) {
    for (const s of STOCK_FALLBACK_IMAGES) {
      if (owned.length >= MAX_CANDIDATES) break;
      push({
        id: `stock-${owned.length}`,
        url: s.url,
        label: s.label,
        source: 'stock',
      });
    }
  }

  return owned;
}
